import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { NxFlatTreeControl, NxFlatTreeNode, NxTreeFlatDataSource, NxTreeNode } from '@aposin/ng-aquila/tree';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest, map, takeUntil, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MenuItem } from '../../../model/globtroter-config';
import { AppState } from '../../../store';
import * as fromConfig from '../../../store/config/config.reducer';
import { staticGlobtroterPaths } from '../static-globtroter-paths';

export interface MyTreeNode extends NxTreeNode {
  children?: MyTreeNode[];
  label: string;
  link?: string;
  isSpaLink?: boolean;
}

interface MyFlatTreeNode extends NxFlatTreeNode {
  label: string;
  link?: string;
  isSpaLink?: boolean;
}

@Component({
  selector: 'app-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileMenuComponent implements OnDestroy {
  readonly staticGlobtroterPaths = staticGlobtroterPaths;
  dataSource!: NxTreeFlatDataSource<MyTreeNode, MyFlatTreeNode>;
  treeControl: NxFlatTreeControl<MyFlatTreeNode>;
  globtroterApp = environment.globtroterApp;
  globtroterMenuConfig$ = this.store.select(fromConfig.selectGlobtroterMenuConfig);
  globtroterUserName$ = this.store.select(fromConfig.selectGlobtroterUserName);
  destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>, private translateService: TranslateService) {
    this.treeControl = new NxFlatTreeControl();
    combineLatest([this.globtroterMenuConfig$, this.globtroterUserName$])
      .pipe(
        takeUntil(this.destroy$),
        map(([navItems, userName]) => {
          if (navItems) {
            return [
              {
                label: userName,
                type: 'menu',
                items: [
                  {
                    label: this.translateService.instant('globtroter-menu.news') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.news,
                    isSpaLink: true,
                  },
                  {
                    label: this.translateService.instant('globtroter-menu.user-profile') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.userProfile,
                    isSpaLink: true,
                  },
                  {
                    label: this.translateService.instant('globtroter-menu.change-password') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.changePassword,
                    isSpaLink: true,
                  },
                  {
                    label: this.translateService.instant('globtroter-menu.logout') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.logout,
                    isSpaLink: true,
                  },
                ],
              },
              ...navItems,
            ] as MenuItem[];
          }

          return undefined;
        }),
        map((navItems) => navItems?.map((item) => this.mapMenuItemToTreeNode(item))),
        tap((x) => {
          this.dataSource = new NxTreeFlatDataSource(this.treeControl, x);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasChild = (_: number, node: NxFlatTreeNode) => node.expandable;

  private mapMenuItemToTreeNode(menuItem: MenuItem): MyTreeNode {
    return {
      label: menuItem.label,
      link: menuItem.link,
      isSpaLink: menuItem.isSpaLink,
      children: menuItem.items?.map((item) => this.mapMenuItemToTreeNode(item)),
    };
  }
}
